<template>
  <slot
    name="header"
    :manage-workers="manageWorkers"
    :can-configure="canConfigure"
    :refetch="refetch"
  />
  <GraphqlDataTable
    v-model:selected-rows="selectedRows"
    v-model:total-count="totalCount"
    v-model:select-all="selectAll"
    v-model:filters="filters"
    :query
    :order-by="initialOrder"
    :filter-black-list="conditionalFilterBlackList"
    :column-black-list="conditionalColumnBlackList"
    :quick-filters="['statuses']"
    :search-placeholder="$t('Search staffing agencies...')"
    :bulk-actions="bulkActions"
    :contain-bulk-action-bar="props.containBulkActionBar"
    :multi-select="props.multiSelect"
    :is-row-pre-selected="isRowPreSelected"
    v-model:pre-selected-count="preSelectedCount"
    :max-height-adjustment="props.maxHeightAdjustment"
    :default-per-page="props.defaultPerPage"
    :default-per-page-options="props.defaultPerPageOptions"
    preserve-selected-rows
  >
    <template v-if="$slots.action" #action="slotProps">
      <slot name="action" v-bind="slotProps" />
    </template>
  </GraphqlDataTable>
</template>

<script lang="ts" setup>
import { computed, provide } from 'vue'
import { TypedQueryDocumentNode } from 'graphql/index'
import { useQuery } from '@vue/apollo-composable'
import GraphqlDataTable from '@/components/GraphqlDataTable/GraphqlDataTable.vue'
import { graphql } from '@/gql'
import { RecruiterOrderByColumn, SortOrder } from '@/gql/graphql'
import { DataTableBulkAction } from '@/components/GraphqlDataTable/types'
import { publicApi } from '@/app-bootstrapping/SetUpApollo'
import { $t } from '@/composables/translation'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'

const props = withDefaults(
  defineProps<{
    bulkActions?: DataTableBulkAction[]
    containBulkActionBar?: boolean
    multiSelect?: boolean
    isRowPreSelected?: (row: object) => boolean
    maxHeightAdjustment?: number | string
    defaultPerPage?: number
    defaultPerPageOptions?: number[]
  }>(),
  {
    bulkActions: () => [],
    containBulkActionBar: false,
    multiSelect: false,
    isRowPreSelected: undefined,
    maxHeightAdjustment: undefined,
    defaultPerPage: 50,
    defaultPerPageOptions: () => [10, 50, 100],
  },
)

const selectedRows = defineModel<Record<string, object>>('selectedRows')

const totalCount = defineModel<number>('totalCount', { default: 0 })

const selectAll = defineModel<boolean>('selectAll', { default: false })

const preSelectedCount = defineModel<number>('preSelectedCount', { default: 0 })

const filters = defineModel<Record<string, unknown>>('filters', { default: () => ({}) })

const query = graphql(`
  query CompanyRecruitersTableQuery(
    $accounts: [ID!]
    $search: String
    $page: Int!
    $first: Int!
    $orderBy: [RecruiterOrderByClauseInput!]
    $statuses: [RecruiterStatus!]
    $recruiterRegions: MarketRegionInput
    $externalIdentifiers: [String!]
    $companyRecruiterCustomFields: [CustomFieldTypeValueInput!]
    $endClients: [ID!]
  ) {
    companyRecruiters(
      accounts: $accounts
      search: $search
      page: $page
      first: $first
      orderBy: $orderBy
      statuses: $statuses
      recruiterRegions: $recruiterRegions
      externalIdentifiers: $externalIdentifiers
      customFields: $companyRecruiterCustomFields
      endClients: $endClients
    ) {
      data {
        id
        ...CompanyRecruiterNameColumn
        ...CompanyRecruiterStatusColumn
        ...CompanyRecruiterFeeColumn
        ...CompanyRecruiterOwnershipColumn
        ...CompanyRecruiterSubmissionLimitColumn
        ...CompanyRecruiterTypeColumn
        ...CompanyRecruiterTagsColumn
        ...CompanyRecruiterEndClientsColumn
      }
    }
  }
`) as TypedQueryDocumentNode

const companySettingsQuery = graphql(`
  query CompanyRecruiterSettingsQuery($companyId: ID!) {
    company(id: $companyId) {
      id
      recruiterManagesMixedMode
      settings {
        staffingAgencyManagesWorker
      }
      staffingAgencies(first: 1) {
        paginatorInfo {
          count
        }
      }
    }
  }
`)

const { result: companyResult } = useQuery(
  companySettingsQuery,
  { companyId: currentAccount.globalId },
  { clientId: 'publicApi' },
)

const { actsAsSupplier } = useAccountCapabilities()

const canConfigure = computed(
  () => companyResult.value?.company?.recruiterManagesMixedMode ?? false,
)

const manageWorkers = computed(
  () => companyResult.value?.company?.settings?.staffingAgencyManagesWorker ?? false,
)

const conditionalColumnBlackList = computed(() => {
  const blackList: string[] = ['id']
  if (!canConfigure.value) {
    blackList.push('CompanyRecruiterTypeColumn')
  }
  // For non-supplier companies every relation's end client is the company itself.
  if (!actsAsSupplier.value) {
    blackList.push('CompanyRecruiterEndClientsColumn')
  }
  return blackList
})

const conditionalFilterBlackList = computed(() => {
  const blackList = ['accounts', 'externalIdentifiers', 'statuses']
  if (!actsAsSupplier.value) {
    blackList.push('endClients')
  }
  return blackList
})

const initialOrder = {
  field: RecruiterOrderByColumn.RecruiterName,
  order: SortOrder.Asc,
}

provide('canConfigureRecruiterManagesWorkers', canConfigure)

function refetch() {
  publicApi.refetchQueries({ include: ['CompanyRecruitersTableQuery'] })
}
</script>
